<template>
  <div>
    <template v-if="loading">
      <div class="import-client">
        <div class="import-client__loading">
          <LoaderIcon />
        </div>
      </div>
    </template>
    <template v-else>
      <ImportClientsStepInvitation
        v-if="!isFormSent && languages"
        :languages="languages"
        @import-client-form="submit"
      />
      <template v-else>
        <ImportClientsStepNoResult
          v-if="response"
          :form="form"
          :response="response"
          :get-new-contacts="getNewContacts"
          :invites-sent="invitesSent"
          @validate-step="changeStep"
          @close-popin="closePopin"
        />
      </template>
    </template>
  </div>
</template>
<script lang="ts">
import { BrandClickedEvent } from '@monolith/legacy/services/analytics/events/brand-clicked.event';
import { BrandClickOrigin, BrandClickAction } from '@monolith/legacy/types/analytics/brand-click-event';
import { mapGetters, mapActions } from 'vuex';
import { captureException } from '@core/plugins/sentry';

import Analytics from '@monolith/legacy/services/analytics';
import { importClients } from '../../../services/api/import-clients-step';
import { type ImportClients } from '../../../types/import-clients.types';

import { defineComponent } from 'vue';

import ImportClientsStepNoResult from './import-clients-step/import-clients-step-no-result.vue';
import ImportClientsStepInvitation from './import-clients-step/import-clients-step-invitation.vue';
import LoaderIcon from '@monolith/legacy/components/loader-icon.vue';
import { LanguageOption } from '@monolith/legacy/types/campaign';
import { AkLocale } from '@core/types/base';
import { getGlobalConfig } from '@monolith/legacy/services/global-config';
import { FeatureFlag, isEnabled } from '@client/monolith/legacy/services/features';
import http from '@monolith/legacy/services/api/http';
import { getFormattedFrom } from '@core/utilities/email';

interface ImportResponse {
  sent_emails: string[];
  already_imported_emails: string[];
  zero_percent_emails: string[];
  zero_percent_lift_emails: string[];
  uninvited_count: number;
}

export default defineComponent({
  name: 'ImportClientsStep',
  components: {
    ImportClientsStepNoResult,
    ImportClientsStepInvitation,
    LoaderIcon,
  },
  emits: ['changeStep', 'closePopin'],
  data(): {
    isFormSent: boolean;
    response: ImportResponse;
    loading: boolean;
    languages: LanguageOption[];
    form: {
      type: string;
      language: string;
      emails: string;
    };
    errors: {
      emails: boolean;
      response: string;
    };
    invitesSent: boolean;
  } {
    return {
      isFormSent: false,
      response: null,
      loading: false,
      languages: null,
      form: {
        type: '',
        language: '',
        emails: '',
      },
      errors: {
        emails: false,
        response: null,
      },
      invitesSent: false,
    };
  },

  computed: {
    ...mapGetters(['brand']),
    getNewContacts() {
      if (this.response !== null) {
        return (this.response?.zero_percent_emails?.length ?? 0) + (this.response?.zero_percent_lift_emails?.length ?? 0);
      }
      return 0;
    },
    getSplittedList(): string[] {
      return this.form.emails.split(/(?:\r\n|\r|\n|\s+|,|;)/g).filter((element) => element !== '');
    },
  },
  created() {
    const openedLocales = getGlobalConfig().opened_locales;
    this.languages = Object.keys(openedLocales).map((locale) => {
      return {
        value: locale as AkLocale,
        label: openedLocales[locale],
      };
    });
  },
  methods: {
    ...mapActions('account/contactManagement', ['reloadTableWithDelay']),
    async submit(formValid) {
      const { sendInvites, ...formData } = formValid;
      this.form = formData;
      this.loading = true;
      try {
        const data: ImportClients = await importClients({
          ...this.form,
          emails: this.getSplittedList,
        });
        this.reloadTableWithDelay(1);

        this.response = data;

        // "sent_emails" is misleadingly named — it's actually all valid, deduplicated emails submitted (no emails sent yet)
        const totalContacts = data.sent_emails?.length ?? 0;
        const alreadyExistingContacts = data.already_imported_emails?.length ?? 0;
        const newContacts = totalContacts - alreadyExistingContacts;

        Analytics.track(
          new BrandClickedEvent({
            brand_id: this.$store.state.brand.id,
            origin: BrandClickOrigin.ImportClientsStep,
            action: BrandClickAction.BrandSaveList,
            value: {
              send_invites_checkbox: sendInvites,
              new_contacts_count: newContacts,
              already_existing_contacts_count: alreadyExistingContacts,
              total_contacts_count: totalContacts,
            },
          })
        );

        if (isEnabled(FeatureFlag.AddContactsAutoInvite) && sendInvites && newContacts > 0) {
          await this.autoSendInvites(newContacts);
        }

        this.loading = false;
        this.isFormSent = true;
      } catch ({ response }) {
        this.loading = false;
        this.errors.response = this.$t('Sorry it seems there is an error');
        captureException(response);
        return false;
      }
      return true;
    },
    async autoSendInvites(newContacts: number) {
      try {
        const templateResponse = await http().get('/api/internal/v1/campaign-templates/brand_introduction', {
          headers: { Accept: 'application/vnd.api+json' },
        });
        const contents = templateResponse.data.data.attributes.contents;
        const content = contents.find((c) => c.language === this.form.language) || contents[0];

        if (!content) {
          return;
        }

        const brand = this.$store.getters.brand;

        await http().post('/api/network/invite-clients', {
          locale: this.form.language,
          from: getFormattedFrom(brand),
          object: content.subject,
          body: content.body + (content.footer ?? ''),
          cta_text: '',
          footer: '',
          include_all_uninvited: false,
        });

        this.invitesSent = true;

        Analytics.track(
          new BrandClickedEvent({
            brand_id: brand.id,
            origin: BrandClickOrigin.EmailsTemplateStep,
            action: BrandClickAction.BrandSaveAndSendInvitation,
            value: {
              invited_contacts_count: newContacts,
            },
          })
        );
      } catch (err) {
        captureException(err);
      }
    },
    changeStep() {
      this.$emit('changeStep', {
        step: 'email_personalization',
        language: this.form.language,
        contactsType: this.form.type,
        nbClients: this.getNewContacts,
        nbClientsLift: this.response.zero_percent_lift_emails.length,
        uninvitedCount: this.response.uninvited_count,
      });
    },
    closePopin() {
      this.$emit('closePopin');
    },
  },
});
</script>
<style lang="scss" scoped>
@import '@css/vue-import';

.import-client {
  &__loading {
    @apply ds-flex ds-items-center ds-justify-center;
    min-height: 400px;
  }
}

.errors {
  @apply ds-text-error-700;
  margin-bottom: 15px;
  text-align: center;
  font-size: 12px;
  line-height: 12px;
}
</style>
